import React from 'react';
import type {
  ReinstatementPaymentImpact,
  RemainingPaymentAction,
} from '../api/claims';
import Modal from './ModalTrue';

interface ReinstatementPaymentModalProps {
  impact: ReinstatementPaymentImpact;
  submitting: boolean;
  onChoose: (action: RemainingPaymentAction) => void;
  onClose: () => void;
}

// Same look as ChargeReviewModal (the cancellation charge review).
const cardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  padding: 24,
  maxWidth: 560,
  width: '100%',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 0',
  borderBottom: '1px solid #e2e8f0',
  gap: 12,
};

const closedBadgeStyle: React.CSSProperties = {
  background: '#f1f5f9',
  color: '#475569',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.3,
  whiteSpace: 'nowrap',
};

const totalsStyle: React.CSSProperties = {
  marginTop: 16,
  paddingTop: 12,
  borderTop: '2px solid #cbd5e1',
  fontSize: 14,
  color: '#0f172a',
};

const infoStyle: React.CSSProperties = {
  marginTop: 16,
  padding: '10px 12px',
  background: '#eff6ff',
  border: '1px solid #bfdbfe',
  borderRadius: 8,
  fontSize: 13,
  color: '#1e3a8a',
};

const buttonRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  marginTop: 20,
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
};

const buttonBase: React.CSSProperties = {
  padding: '10px 18px',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  border: 'none',
};

const pounds = (amount: number | null): string =>
  `£${(amount ?? 0).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const ReinstatementPaymentModal: React.FC<ReinstatementPaymentModalProps> = ({
  impact,
  submitting,
  onChoose,
  onClose,
}) => {
  const remainingIds = impact.remainingClaimIds || [];
  const disabledStyle: React.CSSProperties = submitting ? { opacity: 0.6, cursor: 'not-allowed' } : {};
  return (
    <Modal onClose={submitting ? () => undefined : onClose}>
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 4px 0', fontSize: 18, color: '#0f172a' }}>
            Payment for the remaining closed claims
          </h3>
          <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#475569' }}>
            Reinstating <strong>{impact.claimId}</strong> removes it from payment{' '}
            <strong>{impact.paymentId}</strong>. The existing payment for{' '}
            <strong>{pounds(impact.currentAmount)}</strong> cannot be reduced in place.
          </p>

          {remainingIds.length > 0 && (
            <div>
              {remainingIds.map(claimId => (
                <div key={claimId} style={rowStyle}>
                  <div style={{ fontWeight: 600, color: '#0f172a' }}>{claimId}</div>
                  <span style={closedBadgeStyle}>STAYS CLOSED</span>
                </div>
              ))}
            </div>
          )}

          <div style={totalsStyle}>
            {impact.remainingClaimCount} claim{impact.remainingClaimCount === 1 ? '' : 's'} will
            remain closed. Do you still want to bill the client{' '}
            <strong>{pounds(impact.remainingAmount)}</strong> for those files?
          </div>

          {impact.isManualPayment && (
            <div style={infoStyle} role="note">
              This is a <strong>manual payment link</strong>. Continuing to bill creates a new manual
              link at the same rate per claim. The client is <strong>not</strong> emailed: you will
              be given the new link to send them.
            </div>
          )}

          <div style={buttonRowStyle}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{ ...buttonBase, background: '#f1f5f9', color: '#334155', ...disabledStyle }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onChoose('waive_remaining')}
              disabled={submitting}
              style={{
                ...buttonBase,
                background: '#fff',
                color: '#334155',
                border: '1px solid #cbd5e1',
                ...disabledStyle,
              }}
            >
              Reinstate and waive remaining payment
            </button>
            <button
              type="button"
              onClick={() => onChoose('continue_billing')}
              disabled={submitting}
              style={{ ...buttonBase, background: '#2563eb', color: '#fff', ...disabledStyle }}
            >
              {submitting
                ? 'Updating payment…'
                : `Reinstate and continue billing ${pounds(impact.remainingAmount)}`}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ReinstatementPaymentModal;
